import type { IBillEstimation, IBillEstimationDashboardRow } from '@/types/eos/BillEstimation'
import type { IEnergyBillEstimationFilters, IEnergyBillEstimationGenerationFilters } from '@/types/eos/filters'
import type { ICreateEnergyBillEstimationForm, IUpdateEnergyBillEstimationForm } from '@/types/eos/forms'
import type { IPage } from '@/types/shared/pagination'
import axios from 'axios'

/**
 * Builds FormData from a billing estimation form
 *
 * @param form the billing estimation form (create or update)
 * @returns FormData instance
 */
function buildBillingEstimationFormData(form: ICreateEnergyBillEstimationForm): FormData {
  const formData = new FormData()

  // Append common fields
  formData.append('site', form.site)
  formData.append('year', form.period.getFullYear().toString())
  formData.append('month', (form.period.getMonth() + 1).toString())

  // Append optional numeric fields
  if (form.ncp_demand !== undefined) {
    formData.append('ncp_demand', form.ncp_demand.toString())
  }
  if (form.meter_prior !== undefined) {
    formData.append('meter_prior', form.meter_prior.toString())
  }
  if (form.meter_current !== undefined) {
    formData.append('meter_current', form.meter_current.toString())
  }
  if (form.actual_value !== undefined) {
    formData.append('actual_value', form.actual_value.toString())
  }

  // Append fees array
  form.fees.forEach((fee, index) => {
    if (fee.name) formData.append(`fees[${index}]name`, fee.name)
    if (fee.amount !== undefined && fee.amount !== null) formData.append(`fees[${index}]amount`, fee.amount.toString())
  })

  // Append attachments (only new files with raw property)
  form.attachments.forEach((file, index) => {
    if (file.raw) formData.append(`attachments[${index}]`, file.raw)
  })

  return formData
}

/**
 * Gets paginated energy bill estimations
 *
 * @param {IEnergyBillEstimationFilters} filters
 * @returns paginated RSA versions.
 */
export async function getEnergyBillEstimations(
  filters?: IEnergyBillEstimationFilters
): Promise<IPage<IBillEstimationDashboardRow>> {
  const res = await axios.get(import.meta.env.VITE_APP_ENERGY_BILL_ESTIMATIONS_ENDPOINT, {
    params: filters,
  })
  return res.data
}

/**
 * Generate an estimation
 *
 * @param {IEnergyBillEstimationFilters} filters
 * @returns Energy Bill Estimation as JSON or BLOB depending on response format
 */
export async function generateEnergyBillEstimation(
  filters: IEnergyBillEstimationGenerationFilters
): Promise<Blob | IBillEstimation> {
  // Build filters to match the expected backend ones
  const newFilters = {
    month: filters.period.getMonth() + 1, // Months are zero-based, so we add 1,
    year: filters.period.getFullYear(),
    site: filters.site,
    response_format: filters.response_format,
  }

  const res = await axios.get(`${import.meta.env.VITE_APP_ENERGY_BILL_ESTIMATIONS_ENDPOINT}/generation`, {
    params: newFilters,
    responseType: newFilters.response_format === 'json' ? 'json' : 'blob',
  })

  return res.data
}

/**
 * Delete an estimation
 *
 * @param id the estimation ID
 */
export async function deleteEnergyBillEstimation(id: number): Promise<any> {
  await axios.delete(import.meta.env.VITE_APP_ENERGY_BILL_ESTIMATIONS_ENDPOINT, {
    data: { id },
  })
}

/**
 * Creates a new energy billing estimation
 *
 * @param form create billing estimation form
 * @returns {Promise<any>} created visitor log.
 * @throws {AxiosError}
 */
export async function createEnergyBillingEstimation(form: ICreateEnergyBillEstimationForm): Promise<any> {
  const formData = buildBillingEstimationFormData(form)

  const res = await axios.post(import.meta.env.VITE_APP_ENERGY_BILL_ESTIMATIONS_ENDPOINT, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

/**
 * Updates an existing energy billing estimation
 *
 * @param id the estimation ID
 * @param form update billing estimation form
 * @returns {Promise<any>} updated estimation.
 * @throws {AxiosError}
 */
export async function updateEnergyBillingEstimation(form: IUpdateEnergyBillEstimationForm): Promise<any> {
  const formData = buildBillingEstimationFormData(form)

  // Append update-specific fields
  formData.append('id', form.id.toString())

  // Append deleted attachment IDs
  form.deleted_attachment_ids.forEach((id) => {
    formData.append('deleted_attachment_ids', id.toString())
  })

  const res = await axios.put(`${import.meta.env.VITE_APP_ENERGY_BILL_ESTIMATIONS_ENDPOINT}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}
